// Profitability for the Cost Report's By cost type view: what each cost type
// cost, what it has invoiced, what is still to invoice, what can never be
// invoiced, and the margin.
//
// What went out comes off the invoices in DocuSign, not the Billing tab's
// summaries: a job can be invoiced with no summary built in Fusion at all
// (54026071's invoices were built by script), and the cover on each approval
// is what the client is paying from. billingInvoiceApprovals reads each
// cover's SUMMARY OF CHARGES into lines by area (Labor, Per Diem, Equipment +
// fuel, Third party, Handling fee, Sales tax, Mobilization, a credit) with
// the work period it bills.
//
// A sent invoice — out for approval or approved, less one a later invoice
// replaces — claims the billable cost lines of each of its areas whose work
// date falls in its period. Those lines are Invoiced at what Fusion prices
// them at, and the gap between that and what the cover billed for the area
// is its own row (actual wage, credits baked in, rate fixes), so Invoiced
// ties to the invoices. An invoice with no period (a third-party rebill)
// can't be tied to lines: its amount moves from To Be Invoiced to Invoiced as
// one row under its area.
//
// Mobilization is an advance later invoices apply against: it is Invoiced,
// and comes off To Be Invoiced, so it adds nothing to revenue. An invoice
// applied against it (F00294323, amount due $0.00) bills its work in its own
// areas, so what it applied moves back from Invoiced to To Be Invoiced on the
// mobilization's rows. Sales tax is collected for the state, so not revenue;
// the view states it under the total instead.

import { pNum } from '../db/company';
import { LABOR_BILLABLE_CT } from './billingPricing';
import { fuelLineAmt } from './billingFuel';

// The areas the view groups cost types under, in the order an invoice lists
// them. Labor holds every labor cost type but per diem — the payroll taxes,
// burden and truck pay it can't bill sit beside the hours that do, so the
// area's margin is labor's real margin.
export const AREAS = [
    { k: 'labor', label: 'Labor' },
    { k: 'perdiem', label: 'Per diem' },
    { k: 'equipment', label: 'Equipment' },
    { k: 'thirdparty', label: 'Third party' },
];
const PER_DIEM_CT = '51500';

/** The area a cost line bills under. */
export const lineArea = (t) => {
    const ct = String(t?.ct || '');
    if (ct === PER_DIEM_CT) return 'perdiem';
    if (t?.cls === 'Labor' || LABOR_BILLABLE_CT.includes(ct)) return 'labor';
    if (t?.cls === 'Equipment') return 'equipment';
    return 'thirdparty';
};

/**
 * A line that bills: not held off the invoice, not a never-billed cost type,
 * priced (a no-rate line bills nothing until it is mapped), and, for labor,
 * one of the cost types labor bills on (511 / 512 / 515) — the Billing tab's
 * ready-to-invoice rule.
 */
export const billsLine = (t) => !t.nb && !t.neverBilled && !t.noRate
    && (t.cls !== 'Labor' || LABOR_BILLABLE_CT.includes(String(t.ct || '')));

/**
 * Cost that will never be invoiced: never-billed cost types, lines held off
 * the invoice, and labor cost outside 511 / 512 / 515. A rental voucher whose
 * unit bills on its HeavyJob hours (`ruUnit`) is off the invoice only because
 * its usage lines bill for it, and a no-rate line can still be priced, so
 * neither counts.
 */
export const cantInvoice = (t) => !!t.neverBilled || (!!t.nb && !t.ruUnit)
    || (t.cls === 'Labor' && !LABOR_BILLABLE_CT.includes(String(t.ct || '')));

const COVER_AREA = {
    Labor: 'labor',
    'Per Diem': 'perdiem',
    Equipment: 'equipment', 'Equipment + fuel': 'equipment', 'Fuel surcharge': 'equipment',
    'Third party': 'thirdparty', 'Handling fee': 'thirdparty', Freight: 'thirdparty', 'Pass-through': 'thirdparty',
    'Sales tax': 'tax',
    Mobilization: 'mob',
};
/** Where a cover's charge line lands: an area, 'tax', 'mob', 'credit' or 'other'. */
export const coverArea = (a) => COVER_AREA[a] || (/credit/i.test(String(a || '')) ? 'credit' : 'other');

/** The invoices that have gone out: out for approval or approved, less any a later one replaces. */
export const sentInvoices = (rows) => {
    const live = (rows || []).filter((r) => r.status === 'Out' || r.status === 'Approved');
    const replaced = new Set(live.map((r) => String(r.replaces || '').toUpperCase()).filter(Boolean));
    return live.filter((r) => !replaced.has(String(r.no || '').toUpperCase()));
};

/**
 * Which lines the sent invoices claim, and what the covers billed beyond
 * them. Returns { lineInv: { key: invoice no }, rev(t), adj: { area:
 * { reprice, unmatched, invs } }, extras: { credit, other, tax, mob },
 * mob: { invs: [{ no, amt }], applied: [{ no, amt }] }, unread: [invoice
 * nos whose cover isn't read yet], invs, ties: [{ no, area, from, to,
 * billed, priced, lines }] — every cover charge, oldest period first }.
 */
export const invoiceClaims = (txns, invoiceRows, fuelCtx) => {
    const invs = sentInvoices(invoiceRows);
    const rev = (t) => pNum(t.bill) + fuelLineAmt(t, fuelCtx || {});
    const lineInv = {};
    const adj = {};
    const extras = { credit: 0, other: 0, tax: 0, mob: 0 };
    const unread = [];
    const mobInvs = [];
    // Each cover charge by invoice and area, with what the lines it claimed
    // price at — the export's Invoices sheet ties them out.
    const ties = [];
    // Oldest period first, so a line two invoices could both claim goes to
    // the one that billed it first.
    const ordered = invs.slice().sort((a, b) => String(a.from || '9').localeCompare(String(b.from || '9')));
    ordered.forEach((r) => {
        const charges = r.lines || [];
        if (!charges.length) { unread.push(r.no); return; }
        const byArea = {};
        charges.forEach((c) => {
            const a = coverArea(c.area);
            byArea[a] = (byArea[a] || 0) + pNum(c.amount);
        });
        if (byArea.mob) mobInvs.push({ no: r.no, replaces: r.replaces, amt: byArea.mob });
        Object.entries(byArea).forEach(([a, amt]) => {
            const tie = { no: r.no, area: a, from: r.from || '', to: r.to || '', billed: amt, priced: 0, lines: 0 };
            ties.push(tie);
            if (a in extras) { extras[a] += amt; return; }
            const x = adj[a] || (adj[a] = { reprice: 0, unmatched: 0, invs: [] });
            if (!x.invs.includes(r.no)) x.invs.push(r.no);
            if (!r.from || !r.to) { x.unmatched += amt; return; }
            let priced = 0;
            (txns || []).forEach((t) => {
                if (lineInv[t.key] || !billsLine(t) || lineArea(t) !== a) return;
                const d = String(t.date || '').slice(0, 10);
                if (!d || d < r.from || d > r.to) return;
                lineInv[t.key] = r.no;
                priced += rev(t);
                tie.lines += 1;
            });
            tie.priced = priced;
            x.reprice += amt - priced;
        });
    });
    // What later invoices applied against a mobilization (or the one it
    // replaced): the part of their total the client doesn't pay again.
    const up = (x) => String(x || '').toUpperCase();
    const mobNos = new Set(mobInvs.flatMap((m) => [up(m.no), up(m.replaces)]).filter(Boolean));
    const applied = invs
        .filter((r) => r.applied && mobNos.has(up(r.applied)) && (r.lines || []).length && r.total != null && r.amountDue != null)
        .map((r) => ({ no: r.no, amt: pNum(r.total) - pNum(r.amountDue) }))
        .filter((x) => Math.abs(x.amt) >= 0.005);
    return { lineInv, rev, adj, extras, mob: { invs: mobInvs, applied }, unread, invs, ties };
};

const zero = () => ({ lines: 0, hrs: 0, cost: 0, cant: 0, inv: 0, open: 0 });
const add = (n, x) => {
    n.lines += x.lines; n.hrs += x.hrs; n.cost += x.cost; n.cant += x.cant; n.inv += x.inv; n.open += x.open;
};
/** Revenue, margin and margin % on a node. An adjustment row has no cost of its own, so no margin %. */
export const withMargin = (n) => {
    const revenue = n.inv + n.open;
    const margin = revenue - n.cost;
    return { ...n, revenue, margin, pct: revenue && !n.adj ? margin / revenue : null };
};

/**
 * What a line is, for the view's third level: the person on a labor or per
 * diem line, the unit on an equipment line, the vendor and what it sold on
 * anything else (Steve 10/27: "when you expand it, can it be the details
 * instead of the phase code?").
 */
export const lineDetail = (t) => {
    const a = lineArea(t);
    const d = a === 'labor' || a === 'perdiem' ? t.who || t.jde || t.ds
        : a === 'equipment' ? t.eqDs || t.eq || t.ds || t.who
        : [t.who || t.sup, t.ds].filter(Boolean).join(' — ');
    return String(d || '').trim() || '(no detail)';
};

/**
 * The view's tree: area → cost type → detail (lineDetail), each node { lines, hrs,
 * cost, cant, inv, open, revenue, margin, pct }. `claims` is invoiceClaims'
 * result; `withAdj` puts its rows in (only under All — a Billable chip
 * shows part of the cost, and the invoices are whole-job).
 * Returns { groups, total, extras, unread }.
 */
export const profitTree = (rows, claims, { withAdj = true } = {}) => {
    const areas = {};
    const areaOf = (k) => areas[k] || (areas[k] = { ...zero(), types: {} });
    (rows || []).forEach((t) => {
        const c = pNum(t.cost);
        const bills = billsLine(t);
        const r = bills ? claims.rev(t) : 0;
        const x = {
            lines: 1, hrs: pNum(t.hrs), cost: c, cant: cantInvoice(t) ? c : 0,
            inv: bills && claims.lineInv[t.key] ? r : 0,
            open: bills && !claims.lineInv[t.key] ? r : 0,
        };
        const ar = areaOf(lineArea(t));
        const ct = t.ct || '(no cost type)';
        const ty = ar.types[ct] || (ar.types[ct] = { ...zero(), key: ct, ct, ctDesc: t.ctDesc || '', codes: {} });
        if (!ty.ctDesc && t.ctDesc) ty.ctDesc = t.ctDesc;
        const dk = lineDetail(t);
        const de = ty.codes[dk] || (ty.codes[dk] = { ...zero(), key: dk, label: dk });
        [ar, ty, de].forEach((n) => add(n, x));
    });
    const adjRows = (k) => {
        const a = withAdj && claims.adj[k];
        if (!a) return [];
        const out = [];
        if (Math.abs(a.reprice) >= 0.005) {
            out.push({ ...zero(), key: 'adj-reprice', adj: true, inv: a.reprice, label: 'Invoiced above / below these lines\' pricing', tip: `What ${a.invs.join(', ')} billed for these dates beyond what Fusion prices the same lines at (actual wage, credits baked in, rate fixes)` });
        }
        if (Math.abs(a.unmatched) >= 0.005) {
            out.push({ ...zero(), key: 'adj-unmatched', adj: true, inv: a.unmatched, open: -a.unmatched, label: 'Invoiced, not tied to cost lines', tip: `${a.invs.join(', ')} bills no work period, so its amount comes off To Be Invoiced as a whole rather than line by line` });
        }
        return out;
    };
    const byKey = (a, b) => String(a.key).localeCompare(String(b.key));
    const groups = AREAS.map(({ k, label }) => {
        const ar = areas[k];
        const extra = adjRows(k);
        if (!ar && !extra.length) return null;
        const node = ar || { ...zero(), types: {} };
        const kids = Object.values(node.types).sort(byKey).map((ty) => withMargin({
            ...ty,
            label: ty.ct + (ty.ctDesc ? ' — ' + ty.ctDesc : ''),
            // Biggest cost first: a cost type can hold hundreds of people.
            kids: Object.values(ty.codes).sort((a, b) => b.cost - a.cost || byKey(a, b)).map(withMargin),
        }));
        const sum = { ...zero() };
        add(sum, node);
        extra.forEach((e) => add(sum, e));
        return withMargin({ ...sum, id: 'ar' + k, key: k, label, kids: [...kids, ...extra.map(withMargin)] });
    }).filter(Boolean);
    // Credits and charges no area claims ride as their own group: invoiced,
    // and revenue, but no cost line behind them.
    if (withAdj) {
        [['credit', 'Credits on invoices'], ['other', 'Other invoice charges']].forEach(([k, label]) => {
            const amt = claims.extras[k];
            if (Math.abs(amt) < 0.005) return;
            groups.push(withMargin({ ...zero(), id: 'ar' + k, key: k, label, inv: amt, adj: true, kids: [] }));
        });
    }
    // The mobilization: invoiced, and off what is still to invoice, since
    // later invoices draw it down. No revenue of its own.
    const mob = claims.mob;
    if (withAdj && mob && mob.invs.length) {
        const flat = (n) => ({ ...withMargin(n), revenue: 0, margin: 0 });
        const kids = [
            ...mob.invs.map((m) => flat({ ...zero(), key: 'mob-' + m.no, adj: true, inv: m.amt, open: -m.amt, label: `${m.no} — mobilization`, tip: 'An advance later invoices apply against, so it comes off To Be Invoiced and is not revenue' })),
            ...mob.applied.map((a) => flat({ ...zero(), key: 'mobapp-' + a.no, adj: true, inv: -a.amt, open: a.amt, label: `Applied on ${a.no}`, tip: `${a.no}'s work is invoiced in its own areas above and was paid from the mobilization` })),
        ];
        const sum = { ...zero() };
        kids.forEach((k) => add(sum, k));
        groups.push(flat({ ...sum, id: 'armob', key: 'mob', label: 'Mobilization', adj: true, kids }));
    }
    const total = { ...zero() };
    groups.forEach((g) => add(total, g));
    return { groups, total: withMargin(total), extras: claims.extras, unread: claims.unread };
};
